import React from 'react'
import i18next from 'i18next'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery, useTheme, Box } from '@mui/material'

import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import TruncatedText from 'components/global/Typography/TruncatedText'
import { useResponsiveTableContext } from 'packages/ui/Table'
import { createExpandedRowComponent } from 'packages/ui/Table'

import { AvailableCoupon, ExpiredCoupon, UsedCoupon } from '../Card/Widgets'
import { trackEvent } from 'analytics'

interface Coupon {
  id: string
  used: boolean
  valid: boolean
  code: string
  site: { name: string }
  resident: string
  location: string
  lastname: string
  firstname: string
  phone: string
  created_at: string
}

interface Context {
  t: (key: string) => string
}

const Actions: React.FC<{ coupon: Coupon; ctx: Context }> = ({ coupon, ctx }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const responsiveTableContext = useResponsiveTableContext()
  
  return (
    <CardActionsButtonsGroup>
      <CardActionButton
        tooltiptitle={ctx.t('coupons.actions.send_code.button_tooltip')}
        iconlabel={'comment-dots'}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          trackEvent('send_code_button', { code_type: 'h3' })
          navigate(`/codes/h3/${coupon.id}/send-sms`)
        }}
      />
      {isMobile && responsiveTableContext && (
        <CardActionButton
          tooltiptitle={responsiveTableContext.isRowExpanded(coupon.id) ? ctx.t('coupons.actions.collapse_details') : ctx.t('coupons.actions.expand_details')}
          iconlabel={responsiveTableContext.isRowExpanded(coupon.id) ? 'chevron-up' : 'chevron-down'}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            responsiveTableContext.toggleRowExpansion(coupon.id)
          }}
        />
      )}
    </CardActionsButtonsGroup>
  )
}

export const Status: React.FC<{ coupon: Coupon }> = ({ coupon }) => {
  if (!coupon.used && !coupon.valid) {
    return <ExpiredCoupon table />
  }
  if (coupon.used) {
    return <UsedCoupon table />
  }
  if (!coupon.used && coupon.valid) {
    return <AvailableCoupon table />
  }
  return null
}

export const ExpandedRowDetails = createExpandedRowComponent<Coupon>((coupon, t) => [
  {
    key: 'offers',
    label: t('coupons.table.resident'),
    value: coupon.resident || '-'
  },
  {
    key: 'connexions',
    label: t('coupons.table.location_col'),
    value: coupon.location || '-'
  },
  {
    key: 'validity',
    label: t('coupons.table.lastname_col'),
    value: coupon.lastname || '-'
  },
  {
    key: 'phones',
    label: t('coupons.table.firstname_col'),
    value: coupon.firstname || '-'
  },
  {
    key: 'description',
    label: t('coupons.table.phones_col'),
    value: coupon.phone || '-'
  },
  {
    key: 'creation_date',
    label: t('coupons.table.creation_date_col'),
    value: new Date(coupon.created_at).toLocaleDateString(i18next.language)
  }
])

const COLUMNS_CONFIG = [
  {
    name: 'availability',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.status_col')
    },
    value: (coupon: Coupon) => <Status coupon={coupon} />,
    priority: 1
  },
  {
    name: 'codes',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.codes_col')
    },
    value: (coupon: Coupon) => coupon.code,
    priority: 1
  },
  {
    name: 'sites',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.sites_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.site?.name} 
        maxWidth="120px" 
        maxLength={15}
      />
    ),
    priority: 1
  },
  {
    name: 'offers',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.resident')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.resident} 
        maxWidth="130px" 
        maxLength={16}
      />
    ),
    priority: 2
  },
  {
    name: 'connexions',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.location_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.location} 
        maxWidth="110px" 
        maxLength={14}
      />
    ),
    sortBy: 'number_of_devices',
    sortable: true,
    priority: 2
  },
  {
    name: 'validity',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.lastname_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.lastname} 
        maxWidth="110px" 
        maxLength={14}
      />
    ),
    sortBy: 'validity_period',
    sortable: true,
    priority: 4
  },
  {
    name: 'phones',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.firstname_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.firstname} 
        maxWidth="110px" 
        maxLength={14}
      />
    ),
    priority: 4
  },
  {
    name: 'description',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.phones_col')
    },
    value: (coupon: Coupon) => coupon.phone,
    priority: 3
  },
  {
    name: 'creation_date',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.creation_date_col')
    },
    value: (coupon: Coupon) => new Date(coupon.created_at).toLocaleDateString(i18next.language),
    sortBy: 'created_at',
    sortable: true,
    priority: 3
  },
  {
    name: 'actions',
    header: {
      label: (ctx: Context) => (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          width: '100%'
        }}>
          <Box 
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}
            title={ctx.t('coupons.table.actions_col')}
          >
            {ctx.t('coupons.table.actions_col')}
          </Box>
        </Box>
      )
    },
    value: (coupon: Coupon, ctx: Context) => (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        width: '100%'
      }}>
        <Actions coupon={coupon} ctx={ctx} />
      </Box>
    ),
    priority: 1,
    style: { textAlign: 'right' }
  }
]

export default COLUMNS_CONFIG
